import React, { useState, useEffect } from "react";
import Box from "@mui/material/Box";
import { Typography, Card, CardContent, Stack } from "@mui/material";
import { Grid } from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { useNavigate } from "react-router-dom";
import { FP, PageHeader, fpInputSx } from "components/financepro";
import { useApi, useSucursales } from "lib/api";

function Agregargasto() {
  const navigate = useNavigate();
  const { post } = useApi();

  const [fecha, setFecha] = useState(new Date().toISOString().split("T")[0]);
  const [categoria, setCategoria] = useState("");
  const [categoriaInput, setCategoriaInput] = useState("");
  const [categoriasList, setCategoriasList] = useState([]);
  const [descripcion, setDescripcion] = useState("");
  const [valor, setValor] = useState("");
  const [sucursal, setSucursal] = useState(null);
  const sucursalList = useSucursales();
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    post("/api/buscarcategoriasdegasto").then(({ status, data }) => {
      if (status === 200) {
        const cats = (data?.data1 || []).map((c) => String(c)).filter(Boolean);
        setCategoriasList(cats);
      }
    });
  }, []);

  async function guardar() {
    if (!fecha) { alert("Selecciona una fecha"); return; }
    const cat = categoria || categoriaInput;
    if (!cat) { alert("Indica una categoría"); return; }
    const valorNum = parseFloat(valor);
    if (Number.isNaN(valorNum) || valorNum <= 0) { alert("Ingresa un valor válido"); return; }
    if (!sucursal) { alert("Selecciona una sucursal"); return; }

    const sucCode = String(sucursal?.label || sucursal || "").split("-")[0];

    setSaving(true);
    try {
      const { status, data } = await post("/api/agregargasto", {
        fecha: fecha,
        categoria: cat,
        descripcion: descripcion,
        valor: valorNum,
        sucursal: sucCode,
      });
      setSaving(false);
      if (status === 200) {
        alert("Gasto registrado correctamente");
        navigate("/gastos/informegastos");
      } else {
        alert(data?.Error || "No se pudo guardar el gasto");
      }
    } catch (err) {
      setSaving(false);
      alert("Error de red: " + err.message);
    }
  }

  return (
    <Box component="main" sx={{ minHeight: "100vh", bgcolor: FP.color.surface }}>
      <PageHeader
        title="Gastos"
        subtitle="Agregar Gasto"
        cta={
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(-1)}
            sx={{
              borderColor: FP.color.border,
              color: FP.color.textSecondary,
              borderRadius: 1.4,
              px: 2.2,
              height: 38,
              textTransform: "none",
              fontWeight: 700,
              "&:hover": { borderColor: FP.color.navy, bgcolor: FP.color.surfaceAlt },
            }}
          >
            Volver
          </Button>
        }
      />

      <Box sx={{ p: { xs: 2, sm: 2.5, md: 3 }, maxWidth: 900, mx: "auto" }}>
        <Card
          elevation={0}
          sx={{
            borderRadius: 2.5,
            border: `1px solid ${FP.color.border}`,
            bgcolor: FP.color.cardBg,
            boxShadow: FP.shadow.soft,
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              px: { xs: 2, md: 3 },
              py: 1.6,
              borderBottom: `1px solid ${FP.color.border}`,
              bgcolor: "#FAFBFE",
              display: "flex",
              alignItems: "center",
              gap: 1.2,
            }}
          >
            <Box
              sx={{
                width: 30,
                height: 30,
                borderRadius: 1.2,
                bgcolor: FP.color.accentBlueSoft,
                color: FP.color.accentBlue,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <EditOutlinedIcon sx={{ fontSize: 18 }} />
            </Box>
            <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: "0.98rem" }}>
              Datos del gasto
            </Typography>
          </Box>

          <CardContent sx={{ p: { xs: 2, md: 3 } }}>
            <Grid container spacing={2.5}>
              <Grid item xs={12} sm={6}>
                <TextField
                  type="date"
                  label="Fecha"
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  value={fecha}
                  onChange={(e) => setFecha(e.target.value)}
                  sx={fpInputSx}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  freeSolo
                  options={categoriasList}
                  value={categoria}
                  onChange={(e, v) => setCategoria(v || "")}
                  inputValue={categoriaInput}
                  onInputChange={(e, v) => setCategoriaInput(v || "")}
                  renderInput={(params) => (
                    <TextField {...params} label="Categoría" InputLabelProps={{ shrink: true }} sx={fpInputSx} />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Descripción"
                  fullWidth
                  multiline
                  rows={2}
                  value={descripcion}
                  onChange={(e) => setDescripcion(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={fpInputSx}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  type="number"
                  label="Valor"
                  fullWidth
                  value={valor}
                  onChange={(e) => setValor(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ min: 0, step: "0.01" }}
                  sx={fpInputSx}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  disablePortal
                  options={sucursalList}
                  value={sucursal || null}
                  onChange={(e, v) => setSucursal(v)}
                  renderInput={(params) => (
                    <TextField {...params} label="Sucursal" InputLabelProps={{ shrink: true }} sx={fpInputSx} />
                  )}
                />
              </Grid>
            </Grid>

            <Stack direction="row" spacing={1.5} sx={{ mt: 3, justifyContent: "flex-end" }}>
              <Button
                variant="outlined"
                onClick={() => navigate(-1)}
                disabled={saving}
                sx={{
                  borderColor: FP.color.border,
                  color: FP.color.textSecondary,
                  borderRadius: 1.4,
                  textTransform: "none",
                  fontWeight: 700,
                  px: 2.5,
                  "&:hover": { borderColor: FP.color.navy, bgcolor: FP.color.surfaceAlt },
                }}
              >
                Cancelar
              </Button>
              <Button
                variant="contained"
                startIcon={<SaveOutlinedIcon />}
                onClick={guardar}
                disabled={saving}
                sx={{
                  bgcolor: FP.color.navy,
                  color: "#FFFFFF",
                  borderRadius: 1.4,
                  textTransform: "none",
                  fontWeight: 700,
                  px: 2.8,
                  boxShadow: "none",
                  "&:hover": { bgcolor: FP.color.navyDeep, boxShadow: "none" },
                }}
              >
                {saving ? "Guardando..." : "Guardar"}
              </Button>
            </Stack>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
}

export default Agregargasto;
